import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronLeft,
  Copy,
  CornerDownLeft,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react';
import OryaThinkingStatus from '../../documentation/components/OryaThinkingStatus';
import { aiWriteNotice } from '../services/vcNoticeApi';
import { useClampedFixedPanel } from '../utils/noticeFloatingPanel';
import CustomersNoticeTip from './CustomersNoticeTip';
import './CustomersNoticeFmtBar.css';

const THINKING_STEPS = [
  { text: 'Orya está pensando…', duration: 2000 },
  { text: 'Escrevendo o aviso…', duration: 2500 },
  { text: 'Ajustando o tom…', duration: 2500 },
  { text: 'Gerando resposta…', duration: null },
];

export default function CustomersNoticeOryaCompose({
  position,
  noticeTitle = '',
  keepOpenSelector = '.cmc-manual-editor',
  aiWrite = aiWriteNotice,
  contextDraft = null,
  thinkingSteps = THINKING_STEPS,
  onInsert,
  onClose,
}) {
  const promptRef = useRef(null);
  const [view, setView] = useState('welcome'); // welcome | result
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [lastPrompt, setLastPrompt] = useState('');

  const preferred = useMemo(
    () => ({ top: position?.top, left: position?.left }),
    [position?.top, position?.left]
  );
  const { ref: panelRef, coords, clamp } = useClampedFixedPanel(preferred, [
    view,
    suggestion,
    loading,
    error,
    prompt,
  ]);

  useEffect(() => {
    const onDoc = (e) => {
      if (e.target?.closest?.('.cmc-notice-fmt-orya')) return;
      if (keepOpenSelector && e.target?.closest?.(keepOpenSelector)) return;
      onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, keepOpenSelector]);

  useEffect(() => {
    const t = window.setTimeout(() => promptRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [view]);

  const runAi = useCallback(
    async (userPrompt) => {
      const p = String(userPrompt || '').trim();
      if (!p || loading) return;
      setLoading(true);
      setError('');
      setSuggestion('');
      setLastPrompt(p);
      setView('result');
      try {
        const title = String(noticeTitle || '').trim();
        const draft = contextDraft != null ? String(contextDraft) : title ? `Título do aviso: ${title}` : '';
        const data = await aiWrite({ prompt: p, draft });
        setSuggestion(String(data?.content || '').trim());
      } catch (e) {
        setError(e.message || 'Falha ao gerar com a Orya.');
        setSuggestion('');
      } finally {
        setLoading(false);
      }
    },
    [loading, noticeTitle, contextDraft, aiWrite]
  );

  const resizePrompt = useCallback(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = '0px';
    const next = Math.min(el.scrollHeight, 160);
    el.style.height = `${Math.max(28, next)}px`;
  }, []);

  useLayoutEffect(() => {
    resizePrompt();
    clamp();
  }, [prompt, view, resizePrompt, clamp]);

  const onPromptKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading) runAi(prompt);
    }
  };

  return (
    <div
      ref={panelRef}
      className={
        'cmc-notice-fmt-orya cmc-notice-fmt-orya--compose' +
        (view === 'result' ? ' cmc-notice-fmt-orya--result' : '')
      }
      style={{ top: coords.top, left: coords.left }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {view === 'welcome' ? (
        <>
          <div className="cmc-notice-fmt-orya-head cmc-notice-fmt-orya-head--row">
            <div className="cmc-notice-fmt-orya-brand">
              <Sparkles size={15} strokeWidth={1.9} />
              <strong>Orya</strong>
            </div>
            <button type="button" className="cmc-notice-fmt-icon-btn" aria-label="Fechar" onClick={() => onClose?.()}>
              <X size={14} strokeWidth={2} />
            </button>
          </div>
          <div className="cmc-notice-fmt-orya-head cmc-notice-fmt-orya-head--plain">
            <div>
              <div className="cmc-notice-fmt-orya-title">Como posso te ajudar hoje?</div>
              <div className="cmc-notice-fmt-orya-sub">
                Posso responder perguntas e escrever o conteúdo.
              </div>
            </div>
          </div>
          <div className="cmc-notice-fmt-orya-input-row cmc-notice-fmt-orya-input-row--grow">
            <textarea
              ref={promptRef}
              className="cmc-notice-fmt-orya-input cmc-notice-fmt-orya-input--grow"
              value={prompt}
              rows={1}
                  placeholder="Pergunte qualquer coisa"
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={onPromptKeyDown}
            />
            <button
              type="button"
              className="cmc-notice-fmt-orya-send"
              disabled={!prompt.trim() || loading}
              onClick={() => runAi(prompt)}
            >
              <CornerDownLeft size={14} strokeWidth={2} />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="cmc-notice-fmt-orya-head cmc-notice-fmt-orya-head--row">
            <div className="cmc-notice-fmt-orya-brand">
              <Sparkles size={15} strokeWidth={1.9} />
              <strong>Orya</strong>
            </div>
            <button
              type="button"
              className="cmc-notice-fmt-icon-btn"
              aria-label="Fechar"
              onClick={() => onClose?.()}
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
          <div className="cmc-notice-fmt-orya-suggestion">
            {loading ? (
              <OryaThinkingStatus active steps={thinkingSteps} />
            ) : (
              suggestion || error || 'Sem sugestão.'
            )}
          </div>
          <div className="cmc-notice-fmt-orya-feedback">
            <CustomersNoticeTip label="Gerar novamente" placement="top">
              <button
                type="button"
                className="cmc-notice-fmt-icon-btn"
                disabled={loading}
                onClick={() => runAi(lastPrompt || 'Escreva um rascunho claro e profissional para este aviso.')}
              >
                <RefreshCw size={14} strokeWidth={1.85} />
              </button>
            </CustomersNoticeTip>
          </div>
          <div className="cmc-notice-fmt-orya-input-row cmc-notice-fmt-orya-input-row--grow">
            <textarea
              ref={promptRef}
              className="cmc-notice-fmt-orya-input cmc-notice-fmt-orya-input--grow"
              value={prompt}
              rows={1}
              placeholder="Peça à Orya para escrever"
              disabled={loading}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={onPromptKeyDown}
            />
            <button
              type="button"
              className="cmc-notice-fmt-orya-send"
              disabled={!prompt.trim() || loading}
              onClick={() => runAi(prompt)}
            >
              <CornerDownLeft size={14} strokeWidth={2} />
            </button>
          </div>
          <div className="cmc-notice-fmt-orya-actions">
            <button
              type="button"
              className="cmc-notice-fmt-orya-action is-active"
              disabled={!suggestion || loading}
              onClick={() => {
                onInsert?.(suggestion);
                onClose?.();
              }}
            >
              <Check size={14} strokeWidth={2} />
              <span>Inserir</span>
              <CornerDownLeft size={12} strokeWidth={2} className="cmc-notice-fmt-orya-enter" />
            </button>
            <button
              type="button"
              className="cmc-notice-fmt-orya-action"
              disabled={!suggestion || loading}
              onClick={() => {
                if (suggestion) navigator.clipboard?.writeText(suggestion);
              }}
            >
              <Copy size={14} strokeWidth={1.85} />
              <span>Copiar</span>
            </button>
            <button
              type="button"
              className="cmc-notice-fmt-orya-action"
              disabled={loading}
              onClick={() => {
                setView('welcome');
                setSuggestion('');
                setError('');
              }}
            >
              <ChevronLeft size={14} strokeWidth={2} />
              <span>Voltar</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
